import type { CampaignSlotCardData } from "@/components/campaign/campaign-slot-card";
import { flattenSocialPostSceneLeafBlocks, type SocialPostScenePlanV1 } from "@runcheetah/shared";

export type ScheduledPostOutput = CampaignSlotCardData["output"];
export type ScheduledPostDraft = CampaignSlotCardData["draft"];
export type ScheduledPostCompletedAttempt = NonNullable<ScheduledPostOutput["latestCompleted"]>;
export type ScheduledPostCarouselOutput = NonNullable<ScheduledPostCompletedAttempt["carousel"]>;
export type ScheduledPostCarouselSlideOutput = ScheduledPostCarouselOutput["slides"][number];
export type ScheduledPostPreviewLogoAsset = {
  objectKey: string;
  signedUrl: string;
  isPrimary?: boolean;
};

type ScheduledPostPreviewBrandkit = NonNullable<
  NonNullable<ScheduledPostCompletedAttempt["preview"]>["brandkit"]
>;

export const emptyScheduledPostOutput: ScheduledPostOutput = {
  latestAttempt: null,
  latestCompleted: null,
  latestCopy: null,
};

export function resolveCarouselSlidePreview(
  carousel: ScheduledPostCarouselOutput | null | undefined,
  selectedSlideId?: string | null,
  selectedSlideIndex?: number | null,
) {
  if (!carousel || carousel.slides.length === 0) {
    return {
      carousel: null,
      slide: null,
      scenePlan: null,
      plan: null,
      assets: [],
      previewAsset: null,
    };
  }

  const requestedSlide = selectedSlideId
    ? carousel.slides.find((slide) => slide.slide.id === selectedSlideId)
    : selectedSlideIndex
      ? carousel.slides.find((slide) => slide.slide.index === selectedSlideIndex)
      : null;
  const selectedSlide = requestedSlide ?? carousel.slides[0] ?? null;

  return {
    carousel,
    slide: selectedSlide,
    scenePlan: selectedSlide?.scenePlan ?? null,
    plan: selectedSlide?.scenePlan?.plan ?? null,
    assets: selectedSlide?.assets ?? [],
    previewAsset: selectedSlide?.assets[0] ?? null,
  };
}

export function getScheduledPostOutput(output: unknown): ScheduledPostOutput {
  if (output && typeof output === "object" && "latestCompleted" in output) {
    return output as ScheduledPostOutput;
  }

  return emptyScheduledPostOutput;
}

const resolvePlanLogoObjectKey = (plan: SocialPostScenePlanV1 | null): string | null =>
  plan
    ? (flattenSocialPostSceneLeafBlocks(plan.scene.blocks).find((block) => block.type === "logo")
        ?.assetObjectKey ?? null)
    : null;

export function resolveScheduledPostPreviewBrandkit(input: {
  brandkit: ScheduledPostPreviewBrandkit | null;
  plan: SocialPostScenePlanV1 | null;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
}): ScheduledPostPreviewBrandkit | null {
  const logoAssets = input.logoAssets ?? [];
  if (logoAssets.length === 0) {
    return input.brandkit;
  }

  const selectedLogoObjectKey = resolvePlanLogoObjectKey(input.plan);
  const selectedLogo = selectedLogoObjectKey
    ? (logoAssets.find((logo) => logo.objectKey === selectedLogoObjectKey) ?? null)
    : null;
  const fallbackLogo = logoAssets.find((logo) => logo.isPrimary) ?? null;
  const logo = selectedLogo ?? fallbackLogo;

  if (!logo) {
    return input.brandkit;
  }

  return {
    logo: {
      objectKey: logo.objectKey,
      signedUrl: logo.signedUrl,
    },
    fonts: input.brandkit?.fonts ?? [],
  };
}

export function resolveScheduledPostPreview({
  output,
  draft,
  selectedCarouselSlideId,
  selectedCarouselSlideIndex,
  logoAssets,
}: {
  output: unknown;
  draft?: ScheduledPostDraft | null;
  selectedCarouselSlideId?: string | null;
  selectedCarouselSlideIndex?: number | null;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
}) {
  const normalizedOutput = getScheduledPostOutput(output);
  const latestCompleted = normalizedOutput.latestCompleted;
  const carouselPreview = resolveCarouselSlidePreview(
    latestCompleted?.carousel ?? null,
    selectedCarouselSlideId,
    selectedCarouselSlideIndex,
  );
  const metadata =
    normalizedOutput.latestCopy?.result?.metadata ?? latestCompleted?.result?.metadata ?? null;
  const resolvedDraft = draft ?? null;
  const carousel = carouselPreview.carousel;
  const draftCarouselSlide = carouselPreview.slide
    ? (resolvedDraft?.carouselSlides.find(
        (slideDraft) =>
          slideDraft.slideId === carouselPreview.slide?.slide.id ||
          slideDraft.slideIndex === carouselPreview.slide?.slide.index,
      ) ?? null)
    : null;
  const plan = carousel
    ? (draftCarouselSlide?.plan ?? carouselPreview.plan)
    : (resolvedDraft?.plan ?? latestCompleted?.result?.plan ?? null);
  const brandkit = resolveScheduledPostPreviewBrandkit({
    brandkit: latestCompleted?.preview?.brandkit ?? null,
    plan,
    logoAssets,
  });

  return {
    output: normalizedOutput,
    carousel,
    carouselSlide: carouselPreview.slide,
    previewAsset: carousel ? carouselPreview.previewAsset : (latestCompleted?.assets[0] ?? null),
    linkedAssets: carousel ? carouselPreview.assets : (latestCompleted?.assets ?? []),
    brandkit,
    plan,
    caption: resolvedDraft?.caption ?? carousel?.caption ?? metadata?.caption ?? "",
    hashtags: resolvedDraft
      ? resolvedDraft.hashtags
      : (carousel?.hashtags ?? metadata?.hashtags ?? []),
  };
}

export type ScheduledPostPreview = ReturnType<typeof resolveScheduledPostPreview>;
